import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {catchError, finalize, first, switchMap, tap} from 'rxjs/operators';
import {ErrorHandlingService} from 'shared/_services/error-handling.service';
import {NgProgress} from 'ngx-progressbar';
import {Type} from 'io-ts';
import {TypeCheckService} from 'shared/_services/type-check.service';
import {SharedConfig} from 'shared/lib/shared.config.interface';
import {RestOptions} from 'shared/_interface/rest-options.interface';
import {RestPayload} from 'shared/_interface/rest-payload.interface';
import {REST_DEFAULTS} from 'shared/defaults';
import {RestMethods} from 'shared/enums';
import {SharedConfigToken} from 'shared/lib/shared.config.token';

@Injectable({
	providedIn: 'root'
})
export class RestService {
	private isAuthenticated = true;
	constructor(
		private http: HttpClient,
		private errors: ErrorHandlingService,
		private progress: NgProgress,
		private typeCheck: TypeCheckService,
		@Inject(SharedConfigToken) private config: SharedConfig
	) {}
	private getOptions = (opt: RestOptions): RestOptions => Object.assign({}, REST_DEFAULTS, opt);

	private showModelValidation<T>(input: unknown, model: Type<T>) {
		const check = this.typeCheck.printTypeCheck(input, model);
		!this.config.production && !check;
	}

	setAuthenticationStatus(status: boolean) {
		this.isAuthenticated = status;
	}

	getUrl = (url: string, prepend: boolean, mock = false): string =>
		prepend ? `${this.config.http[!mock ? 'apiUrl' : 'mockUrl']}/${url}` : url

	/***
	 * request method should never be used beside facade (which in our case is DataService)
	 */
	request<T>(
		type: RestMethods,
		url: string,
		payload?: RestPayload,
		options: RestOptions = {}
		) {
		if (!this.isAuthenticated) {
			this.progress.ref().complete();
			return throwError(new Error('User is not authenticated'));
		}
		const innerOptions = this.getOptions(options);
		const newUrl = this.getUrl(url, innerOptions.apiPrefix, innerOptions.mockApi);
		const getPayload = () => payload || {};
		const withOpts = (opts: any) => this.http.request<T>(type, newUrl, {...opts, ...getPayload()});
		const clientOpts: any = {
			observe: 'response',
			withCredentials: true,
			credentials: 'include'
		};
		if (options.asBlob) {
			clientOpts.responseType = 'blob';
			clientOpts.headers = {'content-type': 'application/json'};
		}
		return withOpts(clientOpts).pipe(
			tap((s: HttpResponse<T>) => {
				innerOptions.withLoader && this.progress.ref().start();
				innerOptions.modelValidation && this.showModelValidation<T>(s.body, innerOptions.modelValidation);
			}),
			switchMap(v => {
				const res$ = of(innerOptions.fullResponse ? (v as HttpResponse<T>) : (v.body as T));
				return innerOptions.once ? res$.pipe(first()) : res$;
			}),
			catchError(err => {
				return innerOptions.processError ? (this.errors.printError(err) as Observable<HttpErrorResponse>) : throwError(err);
			}),
			finalize(() => {
				innerOptions.withLoader && this.progress.ref().complete();
			})
		);
	}
}
